// pages/Dashboard/styles.js
import styled from "styled-components";
import { motion } from "framer-motion";
import { Container } from "../../styles/components";
import { Button } from "../../styles/components";

export const DashboardWrapper = styled.div`
	min-height: calc(100vh - 72px); // Assuming navbar height is 72px
    padding-bottom: ${props => props.theme.spacing.xl};
    background: ${props => props.theme.colors.background.alt};
    flex-direction: column;
    justify-content: center;
`;


export const DashboardHeader = styled.div`
    background: white;
    padding: ${props => props.theme.spacing.xl};
    margin-bottom: ${props => props.theme.spacing.xxl};

    h1 {
        font-size: ${props => props.theme.typography.sizes["2xl"]};
        margin-bottom: ${props => props.theme.spacing.sm};
        margin-top: ${props => props.theme.spacing.xxl};
        
        @media (max-width: ${props => props.theme.breakpoints.md}) {
            font-size: ${props => props.theme.typography.sizes.xl};
        }
    }

    p {
        color: ${props => props.theme.colors.text.secondary};
    }
`;
export const HeaderContent = styled(Container)`
	h1 {
		font-size: ${(props) => props.theme.typography.sizes["2xl"]};
		margin-top: ${(props) => props.theme.spacing.xxl};
		margin-bottom: ${(props) => props.theme.spacing.sm};
	}

	p {
		opacity: 0.9;
	}
`;

export const StatsGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(1, 1fr);
	gap: ${(props) => props.theme.spacing.lg};
	margin-bottom: ${(props) => props.theme.spacing.xl};

	@media (min-width: ${(props) => props.theme.breakpoints.sm}) {
		grid-template-columns: repeat(2, 1fr);
	}

	@media (min-width: ${(props) => props.theme.breakpoints.lg}) {
		grid-template-columns: repeat(4, 1fr);
	}
`;

export const StatCard = styled(motion.div)`
	background: ${(props) => props.theme.colors.background.main};
	padding: ${(props) => props.theme.spacing.lg};
	border-radius: ${(props) => props.theme.borderRadius.lg};
	box-shadow: ${(props) => props.theme.shadows.sm};

	.icon {
		width: 40px;
		height: 40px;
		border-radius: ${(props) => props.theme.borderRadius.md};
		display: flex;
		align-items: center;
		justify-content: center;
		margin-bottom: ${(props) => props.theme.spacing.md};
		color: ${(props) => props.theme.colors.primary.main};
		background: ${(props) => props.theme.colors.primary.main}15;
	}

	h3 {
		font-size: ${(props) => props.theme.typography.sizes.sm};
		color: ${(props) => props.theme.colors.text.secondary};
		margin-bottom: ${(props) => props.theme.spacing.xs};
	}

	p {
		font-size: ${(props) => props.theme.typography.sizes.xl};
		font-weight: ${(props) => props.theme.typography.weights.bold};
	}
`;

export const Section = styled.section`
	margin-bottom: ${(props) => props.theme.spacing.xl};
`;

export const SectionTitle = styled.h2`
	font-size: ${(props) => props.theme.typography.sizes.xl};
	margin-bottom: ${(props) => props.theme.spacing.lg};
	color: ${(props) => props.theme.colors.text.primary};
`;

export const PhotoGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
	gap: ${(props) => props.theme.spacing.md};
`;

export const PhotoCard = styled(motion.div)`
	aspect-ratio: 1;
	border-radius: ${(props) => props.theme.borderRadius.md};
	overflow: hidden;
	cursor: pointer;
	position: relative;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		transition: transform ${(props) => props.theme.transitions.normal};
	}

	&:hover img {
		transform: scale(1.05);
	}
`;


export const CreditInfo = styled.div`
    background: white;
    border-radius: ${props => props.theme.borderRadius.lg};
    padding: ${props => props.theme.spacing.xl};
    margin-bottom: ${props => props.theme.spacing.xl};
    box-shadow: ${props => props.theme.shadows.md};
    display: grid;
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.xl};

    @media (min-width: ${props => props.theme.breakpoints.md}) {
        grid-template-columns: 3fr 2fr;
    }

    .credit-details {
        h3 {
            font-size: ${props => props.theme.typography.sizes.xl};
            margin-bottom: ${props => props.theme.spacing.md};
        }

        .credit-numbers {
            display: flex;
            align-items: baseline;
            margin-bottom: ${props => props.theme.spacing.lg};
            
            .available {
                font-size: ${props => props.theme.typography.sizes["4xl"]};
                font-weight: ${props => props.theme.typography.weights.bold};
                color: ${props => props.theme.colors.primary.main};
            }

            .separator {
                font-size: ${props => props.theme.typography.sizes["2xl"]};
                margin: 0 ${props => props.theme.spacing.xs};
                color: ${props => props.theme.colors.text.disabled};
            }

            .total {
                font-size: ${props => props.theme.typography.sizes["2xl"]};
                color: ${props => props.theme.colors.text.secondary};
            }

            .unit {
                margin-left: ${props => props.theme.spacing.xs};
                color: ${props => props.theme.colors.text.secondary};
            }
        }
    }

    .credit-info {
        padding-left: ${props => props.theme.spacing.xl};
        border-left: 1px solid ${props => props.theme.colors.border};

        .info-item {
            display: flex;
            justify-content: space-between;
            margin-bottom: ${props => props.theme.spacing.md};
            
            span:first-child {
                color: ${props => props.theme.colors.text.secondary};
            }

            span:last-child {
                font-weight: ${props => props.theme.typography.weights.medium};
            }
        }
    }

    .credit-actions {
        margin-top: ${props => props.theme.spacing.lg};
    }
`;

export const CreditProgress = styled.div`
    background: ${props => props.theme.colors.background.alt};
    border-radius: ${props => props.theme.borderRadius.full};
    height: 8px;
    overflow: hidden;
`;

export const ProgressBar = styled.div`
    height: 100%;
    width: ${props => props.percentage}%;
    background: ${props => props.theme.colors.primary.main};
    transition: width ${props => props.theme.transitions.normal};
`;

export const ActionButton = styled(Button)`
    display: inline-flex;
    align-items: center;
    gap: ${props => props.theme.spacing.sm};
    
    svg {
        transition: transform ${props => props.theme.transitions.fast};
    }

    &:hover svg {
        transform: scale(1.1);
    }
`;

export const TabsContainer = styled.div`
    display: flex;
    gap: ${props => props.theme.spacing.md};
    margin-top: ${props => props.theme.spacing.xl};
    border-bottom: 1px solid ${props => props.theme.colors.border};
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    
    &::-webkit-scrollbar {
        display: none;
    }

    @media (max-width: ${props => props.theme.breakpoints.md}) {
        gap: ${props => props.theme.spacing.sm};
    }
`;

export const TabButton = styled.button`
    padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
    color: ${props => props.active ? props.theme.colors.primary.main : props.theme.colors.text.secondary};
    border-bottom: 2px solid ${props => props.active ? props.theme.colors.primary.main : 'transparent'};
    font-weight: ${props => props.active ? props.theme.typography.weights.semibold : props.theme.typography.weights.regular};
    transition: all ${props => props.theme.transitions.fast};
    white-space: nowrap;

    &:hover {
        color: ${props => props.theme.colors.primary.main};
    }

    @media (max-width: ${props => props.theme.breakpoints.sm}) {
        padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
        font-size: ${props => props.theme.typography.sizes.sm};
    }
`;


